import React, { useEffect } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';

interface PaidRestPeriodsProps {
    context: CollaboratorFormContext;
}

const PaidRestPeriods: React.FC<PaidRestPeriodsProps> = ({ context }) => {
    const { control, watch, setValue } = useFormContext();

    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'paidRestPeriods'
    });

    const formData = watch();
    const paidRestPeriods = formData.paidRestPeriods || [];

    const calculateEndDate = React.useCallback((startDate: string, days: number): string => {
        if (!startDate || !days || days <= 0) return '';

        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + days - 1);

        return end.toISOString().split('T')[0];
    }, []);

    useEffect(() => {
        paidRestPeriods.forEach((period: any, index: number) => {
            if (period.startDate && period.days && period.days > 0) {
                const endDate = calculateEndDate(period.startDate, period.days);
                if (endDate !== period.endDate) {
                    setValue(`paidRestPeriods.${index}.endDate`, endDate);
                }
            }
        });
    }, [paidRestPeriods, setValue, calculateEndDate]);

    if (!shouldShow('paidRestPeriods')) {
        return null;
    }

    const handleAddPeriod = () => {
        append({
            id: `paidrest_${Date.now()}`,
            startDate: '',
            days: 0,
            endDate: '',
            observations: ''
        });
    };

    const handleRemovePeriod = (index: number) => {
        remove(index);
    };

    const handleDateChange = (index: number, date: string) => {
        const currentPeriod = paidRestPeriods[index];
        if (currentPeriod && currentPeriod.days && currentPeriod.days > 0) {
            const endDate = calculateEndDate(date, currentPeriod.days);
            setValue(`paidRestPeriods.${index}.endDate`, endDate);
        }
    };

    const handleDaysChange = (index: number, days: number) => {
        const currentPeriod = paidRestPeriods[index];
        if (currentPeriod && currentPeriod.startDate && days > 0) {
            const endDate = calculateEndDate(currentPeriod.startDate, days);
            setValue(`paidRestPeriods.${index}.endDate`, endDate);
        }
    };

    return (
        <div className="space-y-4" data-testid="paid-rest-periods">
            {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_100px_1fr_2fr_auto] gap-4 items-end p-2 border rounded-md">

                    {/* Data de Início */}
                    <div>
                        <label htmlFor={`paidrest-start-${index}`} className="block text-sm font-medium text-gray-700">
                            Início
                            {isRequired('startDate') && <RequiredIndicator show={true} />}
                        </label>
                        <Controller
                            name={`paidRestPeriods.${index}.startDate`}
                            control={control}
                            rules={{ required: isRequired('startDate') ? 'Data de início é obrigatória' : false }}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            handleDateChange(index, e.target.value);
                                        }}
                                        type="date"
                                        id={`paidrest-start-${index}`}
                                        className="input-primary w-full mt-1"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Quantidade de Dias */}
                    <div>
                        <label htmlFor={`paidrest-days-${index}`} className="block text-sm font-medium text-gray-700">
                            Dias
                            {isRequired('days') && <RequiredIndicator show={true} />}
                        </label>
                        <Controller
                            name={`paidRestPeriods.${index}.days`}
                            control={control}
                            rules={{
                                required: isRequired('days') ? 'Quantidade de dias é obrigatória' : false,
                                min: { value: 1, message: 'Deve ser pelo menos 1 dia' },
                                max: { value: 30, message: 'Máximo 30 dias por período' }
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const numericValue = value === '' ? 0 : parseInt(value, 10);
                                            field.onChange(numericValue);
                                            handleDaysChange(index, numericValue);
                                        }}
                                        type="number"
                                        id={`paidrest-days-${index}`}
                                        min="1"
                                        max="30"
                                        className="input-primary w-full mt-1"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Data Final (Calculada) */}
                    <div>
                        <label htmlFor={`paidrest-end-${index}`} className="block text-sm font-medium text-gray-700">
                            Fim
                        </label>
                        <Controller
                            name={`paidRestPeriods.${index}.endDate`}
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="date"
                                    id={`paidrest-end-${index}`}
                                    readOnly
                                    className="input-primary w-full mt-1 bg-gray-100"
                                />
                            )}
                        />
                    </div>

                    {/* Observações */}
                    <div>
                        <label htmlFor={`paidrest-obs-${index}`} className="block text-sm font-medium text-gray-700">
                            Observações
                        </label>
                        <Controller
                            name={`paidRestPeriods.${index}.observations`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        type="text"
                                        id={`paidrest-obs-${index}`}
                                        className="input-primary w-full mt-1"
                                        placeholder="Ex: Acordo com cliente"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    {/* Botão Remover */}
                    <button
                        type="button"
                        onClick={() => handleRemovePeriod(index)}
                        className="flex items-center justify-center h-10 px-3 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                        title="Remover período"
                        data-testid={`remove-paidrest-${index}`}
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            ))}

            {/* Botão Adicionar Período */}
            <div className="flex justify-start">
                <button
                    type="button"
                    onClick={handleAddPeriod}
                    className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
                    data-testid="add-paidrest-period"
                >
                    <PlusIcon className="h-4 w-4" />
                    Adicionar Período de Descanso
                </button>
            </div>

            {/* Mensagem quando não há períodos */}
            {fields.length === 0 && (
                <div className="text-center py-8 text-gray-500" data-testid="no-paidrest-periods">
                    <p>Nenhum período de descanso programado.</p>
                    <p className="text-sm">Clique em "Adicionar Período de Descanso" para começar.</p>
                </div>
            )}
        </div>
    );
};

export default PaidRestPeriods;
